import firebase from 'firebase/app';
import 'firebase/messaging';
import * as CallAPI from '../api';
import { CONNECTION_FAILED } from '../config/constants';
import { getRegisterDeviceOptionsForFirebase } from '../helper/register-device';
import { UUID } from '../types';

export type FirebaseMessagePayload = {
    data: any;
    from: string;
    notification: {
        title: string;
        body: string;
    };
    priority: 'normal' | 'high' | 'low';
};

export interface FirebaseMessagingOptions {
    userId: UUID;
    deviceId: UUID;
}

export type OnRegisterDeviceCallback = (isDeviceRegistered: boolean) => void;

export type OnMessageCallback = (data: any) => void;

export type OnErrorCallback = (error: any) => void;

export default class FirebaseMessaging {
    private messaging: firebase.messaging.Messaging | null = null;

    private app: firebase.app.App | null = null;

    private firebaseToken: string | null = null;

    private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

    private userId: UUID | null = null;

    private deviceId: UUID | null = null;

    private onRegisterDeviceCallback: OnRegisterDeviceCallback | null = null;

    private onMessageCallback: OnMessageCallback | null = null;

    private onErrorCallback: OnErrorCallback | null = null;

    constructor({ userId, deviceId }: FirebaseMessagingOptions) {
        this.userId = userId;
        this.deviceId = deviceId;
    }

    onRegisterDevice(onRegisterDeviceCallback: OnRegisterDeviceCallback) {
        this.onRegisterDeviceCallback = onRegisterDeviceCallback;
    }

    onMessage(onMessageCallback: OnMessageCallback) {
        this.onMessageCallback = onMessageCallback;
    }

    onError(onErrorCallback: OnErrorCallback) {
        this.onErrorCallback = onErrorCallback;
    }

    initApp() {
        if (firebase.apps.length === 0) {
            const config = {
                apiKey: `${process.env.REACT_APP_FIREBASE_API_KEY}`,
                authDomain: `${process.env.REACT_APP_FIREBASE_AUTH_DOMAIN}`,
                databaseURL: `${process.env.REACT_APP_FIREBASE_DATABASE_URL}`,
                projectId: `${process.env.REACT_APP_FIREBASE_PROJECT_ID}`,
                storageBucket: `${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}`,
                messagingSenderId: `${process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID}`,
                appId: `${process.env.REACT_APP_FIREBASE_APP_ID}`,
                measurementId: `${process.env.REACT_APP_FIREBASE_MEASUREMENT_ID}`,
            };
            this.app = firebase.initializeApp(config);
            this.messaging = firebase.messaging();
            this.messaging.onMessage((payload: FirebaseMessagePayload) => {
                this.onMessageCallback?.(payload.data);
            });
        }
    }

    // eslint-disable-next-line class-methods-use-this
    isServiceWorkerSupported(): boolean {
        return 'serviceWorker' in navigator;
    }

    // eslint-disable-next-line class-methods-use-this
    registerServiceWorker(): Promise<ServiceWorkerRegistration> {
        return navigator.serviceWorker.register(
            `${process.env.PUBLIC_URL}/firebase-messaging-sw.js`,
            {
                scope: `${process.env.PUBLIC_URL}/*`,
            },
        );
    }

    unregisterServiceWorker() {
        if (this.isServiceWorkerSupported() && this.serviceWorkerRegistration) {
            this.serviceWorkerRegistration.unregister();
        }
    }

    async connect() {
        try {
            this.initApp();

            this.serviceWorkerRegistration = await this.registerServiceWorker();

            const isDeviceRegistered = await this.registerDeviceForNotification();
            this.onRegisterDeviceCallback?.(isDeviceRegistered);
        } catch (error) {
            this.onRegisterDeviceCallback?.(false);
            this.onErrorCallback?.(CONNECTION_FAILED);
        }
    }

    private async getToken(serviceWorkerRegistration: ServiceWorkerRegistration) {
        if (!this.messaging) {
            throw new Error('Firebase is not initialized');
        }
        return this.messaging.getToken({ serviceWorkerRegistration });
    }

    async registerDevice(
        userId: UUID,
        deviceId: UUID,
        serviceWorkerRegistration: ServiceWorkerRegistration,
    ) {
        try {
            this.firebaseToken = await this.getToken(serviceWorkerRegistration);
        } catch (error) {
            throw new Error('Unable to get firebase token');
        }

        const { error } = await CallAPI.registerDevice(
            getRegisterDeviceOptionsForFirebase({
                userId,
                firebaseToken: this.firebaseToken,
                deviceId,
            }),
        );

        if (error) {
            throw new Error(error);
        }

        return true;
    }

    async registerDeviceForNotification() {
        if (!this.serviceWorkerRegistration) {
            throw new Error('Service worker is not initialized');
        }

        if (!this.userId) {
            throw new Error('User Id is required to register for notification');
        }

        if (!this.deviceId) {
            throw new Error('Device Id is required to register for notification');
        }

        const isDeviceRegistered = await this.registerDevice(
            this.userId,
            this.deviceId,
            this.serviceWorkerRegistration,
        );
        return isDeviceRegistered;
    }

    private offMessage() {
        if (this.messaging) {
            this.messaging.onMessage(() => {});
        }
    }

    private async removeApp() {
        if (this.app && this.messaging) {
            try {
                await this.messaging.deleteToken();
                await this.app.delete();
            } catch (error) {
                console.log(error);
            }
        }
    }

    async destroy() {
        this.onRegisterDeviceCallback = null;
        this.onMessageCallback = null;
        this.onErrorCallback = null;

        this.userId = null;
        this.deviceId = null;

        this.offMessage();
        await this.removeApp();

        this.unregisterServiceWorker();

        this.serviceWorkerRegistration = null;

        this.firebaseToken = null;

        this.app = null;
        this.messaging = null;
    }
}
